import { NudosMaskedNumberInput } from 'components/NudosComponents';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import { Controller, ControllerRenderProps, useFormContext, useWatch } from 'react-hook-form';
import { IOrderPriceEditionForm } from '../../OrderPriceEdition.types';
import { useEffect, useState } from 'react';
import { CURRENCIES } from 'types/bussines';
import { LOCAL_PRICE_INPUT_MODE } from 'types/global';
import { VOLATILE_CURRENCIES } from 'utils/logisticServices.utils';
import { IOrderProductConfigPrice } from 'types/orders';
import { getOrderProductConfigUsdAmount } from 'utils/orderPriceEdition';

import './EditableProductConfigOrderRow.scss';

interface IEditableProductConfigOrderRowProps {
  registerReference: `sales.${number}.orderProductPrices.${number}`;
}

const EditableProductConfigOrderRow = ({ registerReference }: IEditableProductConfigOrderRowProps) => {
  const { control, getValues, setValue } = useFormContext<IOrderPriceEditionForm>();
  const exchangeRate = useWatch({ control, name: 'exchangeRate' });
  const formCurrency = useWatch({ control, name: 'currency' });
  const productConfig = useWatch({ control, name: registerReference });

  const [localPriceInputMode, setLocalPriceInputMode] = useState<LOCAL_PRICE_INPUT_MODE>(LOCAL_PRICE_INPUT_MODE.HIDDEN);
  const showLocalPriceInput =
    localPriceInputMode === LOCAL_PRICE_INPUT_MODE.EDITABLE || localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY;

  const getLocalPriceInputMode = () => {
    const currency = getValues('currency');
    if (currency === CURRENCIES.USD) {
      setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.HIDDEN);
      return;
    }
    if ((VOLATILE_CURRENCIES as readonly string[]).includes(currency)) {
      setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.EDITABLE);
      return;
    }
    setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.READONLY);
  };

  const handleBlurLocalPrice = (
    text: string = '',
    field: ControllerRenderProps<IOrderPriceEditionForm, `sales.${number}.orderProductPrices.${number}`>
  ) => {
    let newLocalPrice = Number(text) || 0;
    const usdAmount = getOrderProductConfigUsdAmount(field.value);
    if (localPriceInputMode !== LOCAL_PRICE_INPUT_MODE.EDITABLE)
      return field.onChange({ ...field.value, totalValueLocal: newLocalPrice });
    const newExchangeRate = Math.ceil((newLocalPrice / usdAmount) * 100) / 100;
    newLocalPrice = usdAmount * newExchangeRate;
    field.onChange({ ...field.value, totalValueLocal: newLocalPrice, exchangeRate: newExchangeRate });
  };

  const handleObservableConfigFields = () => {
    const usdAmount = getOrderProductConfigUsdAmount(productConfig || {});
    const rateToUse = Number(exchangeRate) || 0;
    const localPrice = usdAmount * rateToUse;
    if (usdAmount > 0 && rateToUse && localPriceInputMode !== LOCAL_PRICE_INPUT_MODE.HIDDEN) {
      setValue(registerReference, {
        ...productConfig,
        totalValueLocal: localPrice,
        exchangeRate: rateToUse
      });
    }
  };

  const getProductConfigConceptLabel = (value: Partial<IOrderProductConfigPrice> | undefined): string => {
    const clsName = value?.configClass?.name;
    const cfgName = value?.configName;
    
    return clsName || cfgName || 'Configuración';
  };

  useEffect(() => {
    handleObservableConfigFields();
  }, [productConfig?.totalUsd, productConfig?.totalValueUsd, productConfig?.priceUsd, exchangeRate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getLocalPriceInputMode();
  }, [formCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Controller
      name={`${registerReference}`}
      control={control}
      render={({ field }) => {
        const conceptLabel = getProductConfigConceptLabel(field.value);
        return (
        <section className="editableProductConfigOrderRow">
          <BordSelectDropdown
            selectOptions={[<span key={String(field.value?.configClassFeeId ?? field.value?.id)}>{conceptLabel}</span>]}
            currentValueOrplaceholder={conceptLabel}
            isFilled={true}
            label="Descripción o concepto"
            isDeactivated={true}
          />
          <NudosMaskedNumberInput
            key={`EditableProductConfigOrderRow-${registerReference}-usd`}
            isFilled={true}
            label="Precio con IVA en USD"
            placeholder="$0.00 USD"
            handleChange={text => {
              const v = text || '';
              field.onChange({ ...field.value, priceUsd: v, totalUsd: v, totalValueUsd: v });
            }}
            numberOfDecimals={2}
            maskPrefix="$"
            maskSufix=" USD"
            currentValue={String(getOrderProductConfigUsdAmount(field.value) || '')}
          />
          {showLocalPriceInput && (
            <NudosMaskedNumberInput
              key={`EditableProductConfigOrderRow-${registerReference}-local`}
              isFilled={true}
              label={`Precio con IVA en ${getValues('currency')}`}
              placeholder={`$0.00 ${getValues('currency')}`}
              handleBlur={text => handleBlurLocalPrice(text, field)}
              numberOfDecimals={2}
              maskPrefix="$"
              maskSufix={` ${getValues('currency')}`}
              currentValue={
                field.value.totalValueLocal != null && field.value.totalValueLocal !== ''
                  ? Number(field.value.totalValueLocal).toFixed(2)
                  : ''
              }
              customClassName="localPriceInput"
              isDeactivated={localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY}
            />
          )}
        </section>
        );
      }}
    />
  );
};

export default EditableProductConfigOrderRow;
